"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Clock,
  CreditCard,
  Eye,
  EyeOff,
  FileSearch,
  Filter,
  Gauge,
  Landmark,
  Lock,
  ScanSearch,
  ShieldCheck,
  Ship,
  UserCheck,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import Breadcrumbs from "@/components/seo/Breadcrumbs";
import type { PublicStats } from "@/lib/api/content";
import { FAQ_ITEMS } from "./faq-data";

const REGISTER_HREF = "/register?role=employer";

export interface FeaturedCandidatePreview {
  id: string;
  rankName: string | null;
  nationality: string | null;
  location: string | null;
  yearsOfExperience: number | null;
  availabilityStatus: string | null;
  vesselType: string | null;
}

// ─── Copy blocks ─────────────────────────────────────────────────────────────

const HIRING_FRICTIONS: { title: string; body: string; Icon: LucideIcon }[] = [
  {
    title: "Candidates you cannot verify",
    body: "A CV arrives by email. The rank looks right, the tanker time looks right, the certificates are attached as scans of scans. Verifying any of it means phone calls, emails to flag administrations, and days of latency per candidate — multiplied across every berth you need to fill. Unverifiable candidates are not a nuisance; they are a per-hire tax on your crewing team's hours.",
    Icon: FileSearch,
  },
  {
    title: "Sourcing that moves at email speed",
    body: "The vacancy goes to three agencies. Each agency searches its own spreadsheet. Shortlists trickle back over days, overlapping and inconsistent, while the vessel's crewing date does not move. By the time you have compared candidates across three inboxes, the best one has signed elsewhere.",
    Icon: Clock,
  },
  {
    title: "No view of the pool before you commit",
    body: "Traditional channels ask for commitment before visibility: sign the agency agreement, pay the retainer, then discover what the market actually holds. You are buying a sight-unseen promise that the candidates you need exist in the numbers you need them.",
    Icon: EyeOff,
  },
  {
    title: "Fees on every single placement",
    body: "Per-placement recruiter fees turn crewing into a perpetual toll. For a manager rotating crews across a fleet, those fees compound into one of the largest line items in marine HR — paid again and again for access to candidates who were findable all along.",
    Icon: CreditCard,
  },
];

const ANONYMOUS_BROWSING_POINTS: { title: string; body: string }[] = [
  {
    title: "Everything needed to judge fit",
    body: "Rank or position, experience band, nationality region, vessel-type families, skills, availability status and earliest joining date. Structured, filterable, comparable — the same fields your crewing team would extract from a CV, already extracted.",
  },
  {
    title: "Nothing that identifies the person",
    body: "No names, no photographs, no contact details, no exact nationality. Until you reveal a candidate, you hold professional signal only. The card is a decision tool, not an identity.",
  },
  {
    title: "A reveal gate, not a paywall ambush",
    body: "When a candidate genuinely interests you, you reveal their identity with a signed token issued to your account — and every reveal is recorded. Reveals are unlimited during your trial. After it, a free allowance applies and a subscription restores full access. The mechanics are explicit; nothing is metered silently.",
  },
  {
    title: "Anonymity that defends itself",
    body: "If a search would match so few candidates that identity could be deduced — one chief engineer with LNG time in a small port state, say — the platform returns nothing rather than risk exposure. Not even a count. Privacy here is enforced by the system, not promised in a policy.",
  },
];

const MATCH_DIMENSIONS: { name: string; weight: number; note: string }[] = [
  { name: "Rank & department", weight: 23, note: "exact rank, seniority ladder, department alignment" },
  { name: "Certification", weight: 18, note: "CoC validity, flag endorsements, STCW coverage" },
  { name: "Skills", weight: 18, note: "declared skills against role requirements" },
  { name: "Experience", weight: 16, note: "sea service or shore tenure, weighted by recency" },
  { name: "Vessel type", weight: 12, note: "exact type → category → class flag, graduated" },
  { name: "Availability", weight: 8, note: "status and joining date against your timeline" },
  { name: "Location", weight: 3, note: "mobilization practicality, not exclusion" },
  { name: "Salary", weight: 2, note: "expectation alignment, lowest weight by design" },
];

const COMPANY_TYPES: { name: string; body: string; Icon: LucideIcon }[] = [
  {
    name: "Ship management companies",
    body: "Fleet-scale crewing is a pipeline problem: dozens of berths, overlapping rotations, officer matrix requirements that differ per vessel and per vetting regime. CrewAhead gives you a filterable pool structured around exactly those constraints — rank, vessel type, certificate validity, availability — with match scores that let a small crewing team cover a large fleet without adding headcount.",
    Icon: Ship,
  },
  {
    name: "Crewing & manning agencies",
    body: "Your product is the shortlist: its speed, its quality, its defensibility. Structured candidate data and per-dimension match scores compress days of sourcing into minutes, and verified document data with expiry tracking means fewer placements that collapse at the flag-endorsement stage. Company profiles carry licence details — including RPSL numbers where required — so principals can see you are properly registered.",
    Icon: Users,
  },
  {
    name: "Direct owners & operators",
    body: "Without a crewing department, every hire has historically meant an agency margin or a personal network. CrewAhead gives a lean owner-operator direct access to the same structured candidate pool the managers use — post the berth, receive matched applicants, message them directly, and keep the recruitment fee in the operating budget.",
    Icon: Landmark,
  },
];

const TRIAL_POINTS = [
  { label: "Trial length", value: "60 days" },
  { label: "Tier during trial", value: "Professional" },
  { label: "Candidate reveals", value: "Unlimited" },
  { label: "Job posts & CV views", value: "999 each" },
];

const EVALUATION_STEPS: { step: string; title: string; body: string }[] = [
  {
    step: "01",
    title: "Register and build the company profile",
    body: "Company type, fleet or client context, and licence details where your jurisdiction requires them — RPSL numbers for Indian recruitment and placement services among them. The 60-day Professional trial activates the moment the profile exists; there is no request form and no sales call in the way.",
  },
  {
    step: "02",
    title: "Post one real vacancy",
    body: "Pick the berth that is currently hardest to fill — that is the honest test. The vacancy takes rank, vessel type, required certificates, trading area, contract terms and salary band as structured fields, the same fields candidates are matched on. Ten minutes of setup, and the role is live to the pool.",
  },
  {
    step: "03",
    title: "Read scored shortlists, not inboxes",
    body: "Applicants arrive with an overall match score, the per-dimension breakdown, and named strengths and gaps against your exact requirements. In parallel, browse the anonymized pool with your own filters to see the market the vacancy is drawing from — supply, experience bands and availability, visible before you reveal anyone.",
  },
  {
    step: "04",
    title: "Reveal, message, decide",
    body: "Reveal the candidates worth a conversation, message them inside the platform, and run your normal interview and document-verification process with structured data in hand. At the end of sixty days you will know precisely what the platform is worth to your crewing operation — because you measured it against your own hiring, not a demo.",
  },
];

const POOL_COMPARISON: { aspect: string; resumeDb: string; crewAhead: string }[] = [
  {
    aspect: "What you actually buy",
    resumeDb: "Static CV documents, uploaded once and rarely maintained",
    crewAhead: "Live structured profiles, maintained because visibility carries no career risk",
  },
  {
    aspect: "Verification burden",
    resumeDb: "Every claim on every CV verified by your team, from scratch",
    crewAhead: "Taxonomy-validated fields and documents with expiry tracking as the starting point",
  },
  {
    aspect: "Candidate freshness",
    resumeDb: "Skews to the unemployed and the actively looking",
    crewAhead: "Includes currently-serving officers who cannot risk a public CV",
  },
  {
    aspect: "Shortlist quality signal",
    resumeDb: "Keyword overlap between your search and their CV stuffing",
    crewAhead: "Eight weighted dimensions with per-dimension scores and named gaps",
  },
  {
    aspect: "Data protection posture",
    resumeDb: "Personal data circulated as email attachments, uncontrolled copies",
    crewAhead: "Pseudonymous until reveal, employer-scoped tokens, every reveal recorded",
  },
  {
    aspect: "Commercial model",
    resumeDb: "Per-placement fees or retainers before you see the pool",
    crewAhead: "60-day full-access trial before any commitment, then a flat subscription",
  },
];


// ─── Motion helpers ──────────────────────────────────────────────────────────

function useInViewOnce(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(el);
    return () => observer.disconnect();
  }, [options, inView]);

  return [ref, inView] as const;
}

function useCountUp(target: number, inView: boolean, reduced: boolean) {
  const [value, setValue] = useState(reduced ? target : 0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }
    if (!inView || startedRef.current) return;
    startedRef.current = true;
    let start: number | undefined;
    const duration = 1400;
    let raf: number;

    const step = (ts: number) => {
      if (start === undefined) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, inView, reduced]);

  return value;
}

function LiveStat({ value, label }: { value: number; label: string }) {
  const [ref, inView] = useInViewOnce({ threshold: 0.3, rootMargin: "-60px" });
  const reduced = useReducedMotion();
  const display = useCountUp(value, inView, !!reduced);

  return (
    <div ref={ref} className="flex flex-col">
      <span className="font-display text-3xl font-bold text-white sm:text-4xl">
        {display.toLocaleString("en-US")}
      </span>
      <span className="mt-1 text-sm text-ash">{label}</span>
    </div>
  );
}

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: reduced ? 0 : 0.45, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Hero animated element: match-score deconstruction ───────────────────────
// A worked example of the platform's actual output: one candidate scored
// against one vacancy across the engine's real weighted dimensions. Bars
// animate to their dimension scores when the panel enters view; the overall
// score counts up. Static at full values under reduced motion.

const HERO_DIMENSIONS: { name: string; score: number }[] = [
  { name: "RANK", score: 100 },
  { name: "CERT", score: 92 },
  { name: "SKILLS", score: 84 },
  { name: "EXP", score: 88 },
  { name: "VESSEL", score: 75 },
  { name: "AVAIL", score: 100 },
  { name: "LOC", score: 60 },
  { name: "SALARY", score: 80 },
];

const HERO_OVERALL = 87;

function MatchBreakdown() {
  const [ref, inView] = useInViewOnce({ threshold: 0.3, rootMargin: "-60px" });
  const reduced = useReducedMotion();
  const overall = useCountUp(HERO_OVERALL, inView, !!reduced);

  return (
    <div ref={ref} className="relative rounded-[2rem] border border-neon-cyan/15 bg-panel/70 p-4 shadow-2xl shadow-neon-cyan/10 backdrop-blur">
      <div className="rounded-[1.5rem] border border-neon-cyan/10 bg-void p-5">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-neon-cyan">
            Match score · worked example
          </p>
          <p className="hidden font-mono text-[11px] text-ash sm:block">
            ENGINE v2 · 8-DIM
          </p>
        </div>

        <div className="mt-4 flex items-end justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">Chief Officer · Oil/Chemical Tanker</p>
            <p className="mt-1 font-mono text-[11px] text-ash">
              CANDIDATE #A-2941 · REVEAL PENDING
            </p>
          </div>
          <div className="text-right">
            <p className="font-display text-4xl font-bold text-neon-green">{overall}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-ash">/ 100 overall</p>
          </div>
        </div>

        <div className="mt-5 space-y-2.5">
          {HERO_DIMENSIONS.map((dim, i) => (
            <div key={dim.name} className="grid grid-cols-[3.5rem_1fr_2.5rem] items-center gap-3">
              <span className="font-mono text-[10px] tracking-wider text-ash">{dim.name}</span>
              <div className="h-1.5 overflow-hidden rounded-full bg-white/8">
                <motion.div
                  className="h-full rounded-full bg-neon-cyan"
                  initial={{ width: reduced ? `${dim.score}%` : "0%" }}
                  animate={inView || reduced ? { width: `${dim.score}%` } : undefined}
                  transition={{ duration: 0.9, delay: reduced ? 0 : 0.15 + i * 0.08, ease: "easeOut" }}
                />
              </div>
              <span className="text-right font-mono text-[10px] text-neon-cyan">{dim.score}</span>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-3 font-mono text-[10px] text-ash">
          <p>
            <span className="text-neon-green">STRENGTHS</span> rank · tanker cert · joining window
          </p>
          <p>
            <span className="text-neon-amber">GAP</span> exact chem-tanker seniority
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── CTA blocks ──────────────────────────────────────────────────────────────

function PrimaryCta({ large = false }: { large?: boolean }) {
  return (
    <Link
      href={REGISTER_HREF}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-neon-cyan font-bold text-obsidian shadow-glow-cyan transition hover:scale-[1.01] hover:bg-neon-cyan/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian ${
        large ? "px-8 py-4 text-base" : "px-7 py-3.5 text-sm"
      }`}
    >
      <Ship className={large ? "h-5 w-5" : "h-4 w-4"} aria-hidden="true" />
      Post your first job free
    </Link>
  );
}

function SecondaryCta({ large = false }: { large?: boolean }) {
  return (
    <Link
      href="#candidate-previews"
      className={`inline-flex items-center justify-center gap-2 rounded-2xl border border-neon-purple/30 bg-neon-purple/8 font-bold text-neon-purple transition hover:border-neon-purple/60 ${
        large ? "px-8 py-4 text-base" : "px-7 py-3.5 text-sm"
      }`}
    >
      <Eye className={large ? "h-5 w-5" : "h-4 w-4"} aria-hidden="true" />
      Browse live candidate previews
    </Link>
  );
}

function SignInLink({ className = "" }: { className?: string }) {
  return (
    <p className={`text-sm text-ash ${className}`}>
      Already have an account?{" "}
      <Link
        href="/login"
        className="font-semibold text-neon-cyan transition-colors hover:text-neon-cyan/80"
      >
        Sign in
      </Link>
    </p>
  );
}

function MidPageCta() {
  return (
    <Reveal className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center gap-5 rounded-[2rem] border border-neon-cyan/15 bg-panel p-8 text-center md:flex-row md:justify-between md:text-left">
        <div>
          <p className="flex items-center justify-center gap-2 text-sm font-semibold text-neon-cyan md:justify-start">
            <Gauge className="h-4 w-4" aria-hidden="true" />
            60-day full-access trial. Evaluated on your own vacancies.
          </p>
          <p className="mt-2 text-lg font-bold text-white">
            The fastest honest evaluation is a real shortlist against a real berth.
          </p>
        </div>
        <div className="flex flex-col items-center gap-3 md:items-end">
          <PrimaryCta />
          <SignInLink />
        </div>
      </div>
    </Reveal>
  );
}

// ─── Featured candidates section ─────────────────────────────────────────────

function FeaturedCandidatesSection({
  candidates,
}: {
  candidates: FeaturedCandidatePreview[] | null;
}) {
  return (
    <section
      id="candidate-previews"
      aria-labelledby="previews-heading"
      className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-green">
            Live pool preview
          </p>
          <h2 id="previews-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
            Real candidates, served live — not a stock mockup.
          </h2>
          <p className="mt-5 text-lg leading-8 text-ash">
            This section is rendered from the platform&apos;s featured-candidates
            feed at request time. What you see below is the same structured,
            privacy-protected presentation your crewing team works with inside
            the directory.
          </p>
        </div>

        {candidates && candidates.length > 0 ? (
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {candidates.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.05}>
                <article className="h-full rounded-xl border border-neon-green/10 bg-panel/70 p-5">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-neon-green">
                      Candidate · revealed to registered employers
                    </p>
                    <Lock className="h-3.5 w-3.5 text-ash" aria-hidden="true" />
                  </div>
                  <p className="mt-3 text-base font-semibold text-foreground">
                    {c.rankName ?? "Maritime professional"}
                  </p>
                  <dl className="mt-3 space-y-1.5 font-mono text-[11px] text-ash">
                    {c.yearsOfExperience !== null && (
                      <div className="flex justify-between">
                        <dt>EXP</dt>
                        <dd className="text-foreground">{c.yearsOfExperience} yrs</dd>
                      </div>
                    )}
                    {c.vesselType && (
                      <div className="flex justify-between">
                        <dt>VESSEL</dt>
                        <dd className="text-foreground">{c.vesselType}</dd>
                      </div>
                    )}
                    {c.nationality && (
                      <div className="flex justify-between">
                        <dt>NAT</dt>
                        <dd className="text-foreground">{c.nationality}</dd>
                      </div>
                    )}
                    {c.location && (
                      <div className="flex justify-between">
                        <dt>LOC</dt>
                        <dd className="text-foreground">{c.location}</dd>
                      </div>
                    )}
                    {c.availabilityStatus && (
                      <div className="flex justify-between">
                        <dt>AVAIL</dt>
                        <dd className="text-neon-green">{c.availabilityStatus}</dd>
                      </div>
                    )}
                  </dl>
                </article>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal className="mt-12">
            <div className="rounded-xl border border-neon-green/15 bg-panel/70 p-8">
              <div className="flex flex-col items-start gap-5 md:flex-row md:items-center md:justify-between">
                <div className="max-w-2xl">
                  <h3 className="flex items-center gap-2 text-lg font-bold text-neon-green">
                    <Lock className="h-5 w-5" aria-hidden="true" />
                    The live feed opens with a free employer account
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-ash">
                    Candidate previews are served from the same endpoint that
                    powers the employer directory, and it answers only
                    authenticated employer requests — a deliberate choice, so
                    that professional data is never exposed to anonymous
                    traffic. Registration is free, the 60-day trial is
                    full-access, and the directory is the first thing you see.
                  </p>
                </div>
                <PrimaryCta />
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function HireCrewContent({
  stats,
  featuredCandidates,
}: {
  stats: PublicStats | null;
  featuredCandidates: FeaturedCandidatePreview[] | null;
}) {
  const prefersReducedMotion = useReducedMotion();

  const heroFade = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 18 },
    visible: { opacity: 1, y: 0 },
  };

  const liveStats = stats
    ? (
        [
          { key: "seafarers", label: "seafarers in the pool", value: stats.seafarers || stats.activeSeafarers },
          { key: "shoreProfessionals", label: "shore professionals", value: stats.shoreProfessionals || stats.activeShoreProfessionals },
          { key: "ranks", label: "ranks structured in matching", value: stats.ranks },
          { key: "certifications", label: "certificate types validated", value: stats.certifications },
          { key: "vesselTypes", label: "vessel types indexed", value: stats.vesselTypes },
          { key: "countries", label: "countries represented", value: stats.countries },
        ] as const
      ).filter((item) => item.value > 0)
    : [];

  return (
    <main className="min-h-screen overflow-hidden bg-obsidian text-foreground">
      <Breadcrumbs />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative px-4 pb-16 pt-10 sm:px-6 sm:pt-14 lg:px-8">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_10%,rgba(0,243,255,0.1),transparent_32%),radial-gradient(circle_at_85%_25%,rgba(168,85,247,0.08),transparent_34%)]"
        />
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroFade}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-neon-cyan/20 bg-neon-cyan/8 px-4 py-2 text-sm font-semibold text-neon-cyan">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Structured profiles · Anonymized pool · 8-dimension matching
            </div>
            <h1 className="max-w-3xl font-display text-4xl font-bold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
              Hire crew you can verify, from a pool you can actually see.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-ash">
              CrewAhead replaces the emailed-CV chain with a structured
              maritime candidate pool: ranks, certificates, sea service and
              availability as validated data, browsable under privacy
              protections, scored against your vacancy across eight weighted
              dimensions. Evaluate the entire platform against your own live
              vacancies with a 60-day full-access trial — before you commit to
              anything.
            </p>
            <div className="mt-9 flex flex-col gap-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <PrimaryCta large />
                <SecondaryCta large />
              </div>
              <SignInLink />
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroFade}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <MatchBreakdown />
          </motion.div>
        </div>
      </section>

      {/* ── Live pool stats ──────────────────────────────────────────────── */}
      {liveStats.length > 0 && (
        <section
          aria-label="Live candidate pool statistics"
          className="border-y border-white/5 bg-panel/30 px-4 py-12 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-neon-green">
              The pool as it stands today — live counts, not projections
            </p>
            <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              {liveStats.map((item) => (
                <LiveStat key={item.key} value={item.value} label={item.label} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Hiring friction ──────────────────────────────────────────────── */}
      <section aria-labelledby="friction-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-magenta">
              The cost of the current model
            </p>
            <h2 id="friction-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              What maritime hiring actually costs you today.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Not the invoice from the agency — the operational drag behind it.
              These are the four frictions that inflate time-to-hire and
              cost-per-hire across the industry, stated plainly because the
              platform was built specifically to remove them.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {HIRING_FRICTIONS.map((friction, i) => (
              <Reveal key={friction.title} delay={i * 0.06}>
                <article className="group h-full rounded-xl border border-white/10 bg-panel/70 p-6 transition-colors hover:border-neon-magenta/25 hover:bg-panel-hover">
                  <div className="flex items-start gap-4">
                    <div className="rounded-lg border border-neon-magenta/15 bg-neon-magenta/8 p-2.5 transition-colors group-hover:bg-neon-magenta/12">
                      <friction.Icon className="h-5 w-5 text-neon-magenta" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">{friction.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-ash">{friction.body}</p>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Evaluation path ──────────────────────────────────────────────── */}
      <section
        aria-labelledby="evaluation-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              How to evaluate us
            </p>
            <h2 id="evaluation-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              A sixty-day evaluation plan you can hold us to.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              B2B platforms ask to be judged on demos and decks. We would
              rather be judged on your hardest open berth. This is the
              sequence most evaluating crewing teams follow — and it is
              deliberately short, because a recruitment platform proves itself
              in weeks or not at all.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {EVALUATION_STEPS.map((item, i) => (
              <Reveal key={item.step} delay={i * 0.07}>
                <article className="h-full rounded-xl border border-white/10 bg-panel/70 p-6 transition-colors hover:border-neon-cyan/25 hover:bg-panel-hover">
                  <p className="font-mono text-sm font-bold text-neon-cyan">{item.step}</p>
                  <h3 className="mt-3 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{item.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Core: anonymized browsing + reveal gate ──────────────────────── */}
      <section
        aria-labelledby="pool-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              The core differentiator
            </p>
            <h2 id="pool-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Browse the whole pool. Identify no one — until it matters.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Every recruitment platform claims a large candidate pool. Most
              mean a resume database: static documents, harvested emails, and
              candidates who stopped updating the day they signed a contract.
              CrewAhead runs a different model. Candidates appear as
              pseudonymous, structured profile cards — full professional
              signal, zero identifying detail — and you reveal identity only
              when a candidate is genuinely worth a conversation. Here is why
              that model produces a better pool, not just a more private one.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {ANONYMOUS_BROWSING_POINTS.map((point, i) => (
              <Reveal key={point.title} delay={i * 0.06}>
                <article className="h-full rounded-xl border border-neon-cyan/10 bg-panel/70 p-6 transition-colors hover:border-neon-cyan/30 hover:bg-panel-hover">
                  <h3 className="text-base font-semibold">{point.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{point.body}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10">
            <div className="rounded-[1.75rem] border border-neon-purple/15 bg-panel/70 p-6 sm:p-8">
              <div className="grid gap-8 md:grid-cols-2">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-neon-purple">
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                    Why anonymity grows the pool
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-ash">
                    The best candidates in maritime are usually employed — and
                    unwilling to post a public CV their current employer might
                    find. Pseudonymous visibility lets a serving chief officer
                    be discoverable to the market without being exposed to
                    their own company. The result is a pool that includes
                    people resume databases structurally cannot reach, kept
                    current because maintaining a profile carries no
                    professional risk.
                  </p>
                </div>
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-neon-purple">
                    <UserCheck className="h-5 w-5" aria-hidden="true" />
                    Why it sharpens your signal
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-ash">
                    A reveal is a deliberate act with a record attached, so it
                    happens when interest is real. Candidates learn their
                    profile earned attention on merit; you learn nothing about
                    anyone you did not actively shortlist. No uncontrolled CV
                    circulation, no candidates discovering their documents in
                    inboxes they never agreed to — and a cleaner data-protection
                    posture for your compliance team to sign off on.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Matching engine ──────────────────────────────────────────────── */}
      <section aria-labelledby="matching-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-green">
              How candidates surface
            </p>
            <h2 id="matching-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Eight weighted dimensions. Not a keyword contest.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Keyword search rewards the CV with the best search-engine
              instincts. CrewAhead&apos;s matching engine scores every
              candidate-job pairing across the dimensions crewing decisions
              are actually made on — each weighted by how much it should
              matter, and each broken out so you can see exactly why a
              candidate ranks where they do.
            </p>
          </div>

          <Reveal className="mt-12">
            <div className="overflow-hidden rounded-xl border border-neon-green/15 bg-panel/70">
              <div className="grid grid-cols-[1fr_4rem_1.6fr] gap-4 border-b border-white/10 bg-white/[0.03] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-ash sm:grid-cols-[1fr_6rem_2fr]">
                <span>Dimension</span>
                <span className="text-right">Weight</span>
                <span>What it evaluates</span>
              </div>
              {MATCH_DIMENSIONS.map((dim) => (
                <div
                  key={dim.name}
                  className="grid grid-cols-[1fr_4rem_1.6fr] items-center gap-4 border-b border-white/5 px-5 py-3.5 transition-colors last:border-0 hover:bg-white/[0.02] sm:grid-cols-[1fr_6rem_2fr]"
                >
                  <span className="text-sm font-semibold text-foreground">{dim.name}</span>
                  <span className="text-right">
                    <span className="inline-block min-w-12 rounded-md border border-neon-green/20 bg-neon-green/8 px-2 py-0.5 text-center font-mono text-xs font-bold text-neon-green">
                      {dim.weight}%
                    </span>
                  </span>
                  <span className="text-sm text-ash">{dim.note}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            <Reveal>
              <article className="h-full rounded-xl border border-white/10 bg-panel/70 p-6">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <Filter className="h-4 w-4 text-neon-green" aria-hidden="true" />
                  Graduated, not binary
                </h3>
                <p className="mt-2 text-sm leading-6 text-ash">
                  Vessel-type fit scores in steps: exact type at the top, same
                  category close behind, shared classification — tanker, gas,
                  offshore, container, bulk, passenger — partial credit,
                  unrelated at a floor. A chief officer with product-tanker
                  time is not interchangeable with a bulk-carrier officer, and
                  the score says so in proportion.
                </p>
              </article>
            </Reveal>
            <Reveal delay={0.06}>
              <article className="h-full rounded-xl border border-white/10 bg-panel/70 p-6">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <ScanSearch className="h-4 w-4 text-neon-green" aria-hidden="true" />
                  Strengths and gaps, named
                </h3>
                <p className="mt-2 text-sm leading-6 text-ash">
                  Every match returns the per-dimension breakdown plus plain
                  language: what the candidate is strong on, and what they are
                  missing for this specific vacancy. Shortlist meetings become
                  comparisons of evidence instead of comparisons of CV
                  formatting — and the reasoning survives an internal audit.
                </p>
              </article>
            </Reveal>
            <Reveal delay={0.12}>
              <article className="h-full rounded-xl border border-white/10 bg-panel/70 p-6">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <BadgeCheck className="h-4 w-4 text-neon-green" aria-hidden="true" />
                  Compliant by construction
                </h3>
                <p className="mt-2 text-sm leading-6 text-ash">
                  Age is deliberately absent from the scoring model — age-based
                  filtering is prohibited under ILO Convention 147 and
                  anti-discrimination law. Dimensions with missing data are
                  excluded and the remainder renormalized, so sparse profiles
                  are scored on evidence rather than penalized for silence.
                </p>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Structured pool vs resume database ───────────────────────────── */}
      <section
        aria-labelledby="comparison-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-purple">
              The honest comparison
            </p>
            <h2 id="comparison-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              A structured pool is not a bigger resume database. It is a
              different instrument.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Most platforms in this category compete on database size. Size
              matters less than what the records are: maintained professional
              profiles behave differently from archived documents in every
              way that affects your hiring outcome. Side by side:
            </p>
          </div>
          <Reveal className="mt-12">
            <div className="overflow-hidden rounded-xl border border-neon-purple/15 bg-panel/70">
              <div className="grid grid-cols-[1fr_1.3fr_1.3fr] gap-4 border-b border-white/10 bg-white/[0.03] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-ash">
                <span>Aspect</span>
                <span>Typical resume database</span>
                <span className="text-neon-cyan">CrewAhead structured pool</span>
              </div>
              {POOL_COMPARISON.map((row) => (
                <div
                  key={row.aspect}
                  className="grid grid-cols-[1fr_1.3fr_1.3fr] gap-4 border-b border-white/5 px-5 py-4 transition-colors last:border-0 hover:bg-white/[0.02]"
                >
                  <span className="text-sm font-semibold text-foreground">{row.aspect}</span>
                  <span className="text-sm leading-6 text-ash">{row.resumeDb}</span>
                  <span className="text-sm leading-6 text-foreground">{row.crewAhead}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <MidPageCta />

      {/* ── Featured candidates (live) ───────────────────────────────────── */}
      <FeaturedCandidatesSection candidates={featuredCandidates} />

      {/* ── By company type ──────────────────────────────────────────────── */}
      <section aria-labelledby="company-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-purple">
              By company type
            </p>
            <h2 id="company-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Managers, agencies and owners hire differently. The platform
              knows.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              A three-hundred-vessel manager and a two-ship owner do not have
              the same crewing problem, and a landing page that pretends
              otherwise is wasting your time. Here is what each of you
              actually gets.
            </p>
          </div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {COMPANY_TYPES.map((company, i) => (
              <Reveal key={company.name} delay={i * 0.06}>
                <article className="h-full rounded-xl border border-neon-purple/10 bg-panel/70 p-6 transition-colors hover:border-neon-purple/30 hover:bg-panel-hover">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-neon-purple/15 bg-neon-purple/8">
                    <company.Icon className="h-5 w-5 text-neon-purple" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold">{company.name}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{company.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Trial & pricing ──────────────────────────────────────────────── */}
      <section
        aria-labelledby="trial-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-amber">
              The commercial terms, plainly
            </p>
            <h2 id="trial-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Sixty days of full access. Evaluated on your vacancies, not our
              demo data.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Every new employer company receives a 60-day trial on the
              Professional tier, activated automatically when your company
              profile is created. No feature gates, no &ldquo;trial
              edition&rdquo; — the platform you evaluate is the platform you
              would pay for, measured against the berths you actually need to
              fill.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {TRIAL_POINTS.map((point, i) => (
              <Reveal key={point.label} delay={i * 0.05}>
                <div className="rounded-xl border border-neon-amber/15 bg-panel/70 p-6">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-ash">
                    {point.label}
                  </p>
                  <p className="mt-2 font-display text-2xl font-bold text-neon-amber">
                    {point.value}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-8">
            <p className="max-w-3xl text-sm leading-7 text-ash">
              When the trial ends, nothing is clawed back: your jobs,
              applications, shortlists and messages remain. The reveal gate
              falls back to a capped free allowance, and a subscription
              restores unlimited access at your plan&apos;s limits. Current
              plans and pricing are listed on the{" "}
              <Link href="/pricing" className="font-semibold text-neon-cyan hover:text-neon-cyan/80">
                pricing page
              </Link>
              . For background on who built this and why, see{" "}
              <Link href="/about" className="font-semibold text-neon-cyan hover:text-neon-cyan/80">
                about CrewAhead
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section aria-labelledby="faq-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              Due diligence
            </p>
            <h2 id="faq-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              The questions procurement and crewing leads ask.
            </h2>
          </div>
          <div className="mt-10 space-y-4">
            {FAQ_ITEMS.map((item, i) => (
              <Reveal key={item.question} delay={i * 0.04}>
                <details className="group rounded-xl border border-white/10 bg-panel/70 p-5 transition-colors open:border-neon-cyan/25 hover:border-neon-cyan/20">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold [&::-webkit-details-marker]:hidden">
                    {item.question}
                    <ArrowRight
                      className="h-4 w-4 shrink-0 text-neon-cyan transition-transform group-open:rotate-90"
                      aria-hidden="true"
                    />
                  </summary>
                  <p className="mt-3 text-sm leading-7 text-ash">{item.answer}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ──────────────────────────────────────────────────── */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-neon-cyan/15 bg-panel p-8 sm:p-12">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 text-sm font-semibold text-neon-cyan">
                <Building2 className="h-4 w-4" aria-hidden="true" />
                Evaluate with evidence, not a sales deck.
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl">
                Post one vacancy. Judge the shortlist. Then decide.
              </h2>
              <p className="mt-4 text-base leading-7 text-ash">
                Sixty days of Professional-tier access, activated the moment
                your company profile exists. Browse the anonymized pool, run
                match scores against a live berth, and reveal the candidates
                worth a conversation. If the shortlist does not make the case,
                the trial cost you nothing to find out.
              </p>
            </div>
            <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:items-center">
              <PrimaryCta large />
              <SecondaryCta />
              <SignInLink className="text-center" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
